import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate } from "../router.js";
import { KeyRound, LoaderCircle, ShieldCheck } from "lucide-react";
import { api, ApiError } from "../api.js";
import { useAuth } from "../auth.js";
import { consoleText, localizedError } from "../console-i18n.js";
import { useLocale } from "../i18n.js";

export function LoginPage() {
  const { user, login } = useAuth();
  const { locale } = useLocale();
  const copy = consoleText(locale).login;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [oidc, setOidc] = useState<{ provider: string; label: string } | null>(null);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    void api<{
      oidc: { provider: string; label: string } | null;
      registration: { enabled: boolean };
    }>("/api/auth/login-methods")
      .then((result) => {
        setOidc(result.oidc);
        setRegistrationEnabled(result.registration.enabled);
      })
      .catch(() => {
        setOidc(null);
        setRegistrationEnabled(false);
      });
  }, []);
  if (user) return <Navigate to="/" replace />;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(
        email,
        password,
        mfaRequired ? (useRecovery ? { recoveryCode } : { mfaCode }) : undefined,
      );
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "mfa-required") setMfaRequired(true);
      else setError(localizedError(locale, caught, copy.failed));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <main className="login-page">
      <section className="login-panel">
        <div className="login-brand">
          <span className="brand-mark">
            <KeyRound size={21} />
          </span>
          <strong>Lobster Verify</strong>
        </div>
        <h1>{copy.title}</h1>
        <p>{copy.description}</p>
        <form onSubmit={(event) => void submit(event)}>
          <label>
            {consoleText(locale).common.email}
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          <label>
            {copy.password}
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          {mfaRequired && !useRecovery ? (
            <label>
              {copy.authenticatorCode}
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                value={mfaCode}
                onChange={(event) => setMfaCode(event.target.value)}
                required
              />
            </label>
          ) : null}
          {mfaRequired && useRecovery ? (
            <label>
              {copy.recoveryCode}
              <input
                autoComplete="one-time-code"
                value={recoveryCode}
                onChange={(event) => setRecoveryCode(event.target.value)}
                required
              />
            </label>
          ) : null}
          {mfaRequired ? (
            <button
              className="text-button auth-switch"
              type="button"
              onClick={() => setUseRecovery((value) => !value)}
            >
              {useRecovery ? copy.useAuthenticator : copy.useRecovery}
            </button>
          ) : null}
          {error ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}
          <button className="primary-button login-submit" type="submit" disabled={submitting}>
            {submitting ? <LoaderCircle className="spin" /> : null}
            {copy.signIn}
          </button>
        </form>
        {!mfaRequired ? (
          <div className="auth-links">
            <Link to="/forgot-password">{copy.forgotPassword}</Link>
            {registrationEnabled ? <Link to="/register">{copy.createAccount}</Link> : null}
          </div>
        ) : null}
        {oidc && !mfaRequired ? (
          <a className="secondary-button oidc-button" href={`/api/auth/oidc/${oidc.provider}`}>
            <ShieldCheck size={17} />
            {copy.continueWith} {oidc.label}
          </a>
        ) : null}
      </section>
    </main>
  );
}
